import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Infinite scroll hook using IntersectionObserver.
 * fetchPage(skip, limit) must return { items, has_more, total }.
 * resetKey: any value — when it changes, the list resets.
 *
 * Robust against:
 *  - Duplicate appends (dedupe by id)
 *  - Stale responses after filter change (generation counter)
 *  - Eager intersection firing (rootMargin tuned + scroll-gated)
 */
export function useInfiniteList(fetchPage, resetKey, pageSize = 12) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null); // null until first fetch resolves
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const skipRef = useRef(0);
  const inflightRef = useRef(false);
  const generationRef = useRef(0);
  const sentinelRef = useRef(null);

  const loadMore = useRef(async () => {});

  loadMore.current = async () => {
    if (inflightRef.current) return;
    const gen = generationRef.current;
    inflightRef.current = true;
    setLoading(true);
    try {
      const data = await fetchPage(skipRef.current, pageSize);
      // Drop stale response if filters changed mid-flight
      if (gen !== generationRef.current) return;
      const incoming = data.items || [];
      setItems((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        const fresh = incoming.filter((i) => !seen.has(i.id));
        skipRef.current = prev.length + fresh.length;
        return prev.concat(fresh);
      });
      setTotal(data.total ?? incoming.length);
      setHasMore(Boolean(data.has_more));
    } catch (e) {
      if (gen === generationRef.current) setError(e);
    } finally {
      if (gen === generationRef.current) {
        setLoading(false);
        inflightRef.current = false;
      }
    }
  };

  // Reset when key changes
  useEffect(() => {
    generationRef.current += 1;
    inflightRef.current = false;
    skipRef.current = 0;
    setItems([]);
    setTotal(null);
    setHasMore(true);
    setError(null);
    loadMore.current();
  }, [resetKey]);

  // IntersectionObserver attaches once; does not depend on loadMore identity
  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !inflightRef.current) {
        loadMore.current();
      }
    }, { rootMargin: "80px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore]);

  return { items, total: total ?? 0, totalReady: total !== null, hasMore, loading, error, sentinelRef };
}
